import { useCallback, useLayoutEffect, useEffect, useRef, useState } from "react";

/**
 * useScreenWakeLock — "화면 꺼짐 시간(화면 유지)" 설정.
 *
 * 웹은 OS 화면 자동 꺼짐 시간을 직접 바꾸지 못한다. 대신 Wake Lock API로
 * 화면을 "안 꺼지게" 잡아둘 수 있다. 여기서는 **마지막 터치 기준**으로 동작:
 *  - 터치/스크롤/키 입력이 있을 때마다 타이머를 리셋하고 wake lock 을 확보
 *  - 마지막 입력 후 선택한 N분 동안 입력이 없으면 wake lock 을 풀어 OS 기본대로 꺼짐
 *  - 0(기본값)이면 아무것도 안 함 → 휴대폰 설정 그대로
 *
 * 설정값은 이 기기 localStorage 에만 저장(기기별). 백그라운드로 갔다 오면
 * (visibilitychange) 자동 재확보한다.
 */
export type KeepAwakeMinutes = 0 | 1 | 3 | 5 | 10;

const STORAGE_KEY = "rmc-screen-keepawake-min";

export const KEEP_AWAKE_OPTIONS: { value: KeepAwakeMinutes; label: string }[] = [
  { value: 0, label: "기본값" },
  { value: 1, label: "1분" },
  { value: 3, label: "3분" },
  { value: 5, label: "5분" },
  { value: 10, label: "10분" },
];

export const keepAwakeLabel = (m: KeepAwakeMinutes): string =>
  KEEP_AWAKE_OPTIONS.find((o) => o.value === m)?.label ?? "기본값";

const wakeLockSupported =
  typeof navigator !== "undefined" && "wakeLock" in navigator;

function readStored(): KeepAwakeMinutes {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    if (v === 1 || v === 3 || v === 5 || v === 10) return v;
  } catch {
    /* ignore */
  }
  return 0;
}

export function useScreenWakeLock() {
  const [minutes, setMinutes] = useState<KeepAwakeMinutes>(readStored);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const timerRef = useRef<number | null>(null);
  const minutesRef = useRef<KeepAwakeMinutes>(minutes);
  useLayoutEffect(() => { minutesRef.current = minutes; }, [minutes]);

  const setOption = useCallback((m: KeepAwakeMinutes) => {
    setMinutes(m);
    try {
      localStorage.setItem(STORAGE_KEY, String(m));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!wakeLockSupported) return;

    let released = false;

    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const releaseLock = () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) void s.release().catch(() => {});
    };

    const acquire = async () => {
      if (released || !minutesRef.current) return;
      if (sentinelRef.current) return;
      if (document.visibilityState !== "visible") return;
      try {
        const s = await navigator.wakeLock.request("screen");
        if (released || !minutesRef.current) {
          void s.release().catch(() => {});
          return;
        }
        sentinelRef.current = s;
        // OS 가 자동으로 풀면(백그라운드 등) 참조만 비운다 → 다음 활동에서 재확보.
        s.addEventListener("release", () => {
          if (sentinelRef.current === s) sentinelRef.current = null;
        });
      } catch {
        /* 권한 거부/미지원 — 조용히 무시 */
      }
    };

    const armTimer = () => {
      clearTimer();
      const m = minutesRef.current;
      if (!m) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        releaseLock(); // 마지막 활동 후 m분 경과 → 화면 풀기
      }, m * 60_000);
    };

    // 활동 감지: wake lock 확보 + 타이머 리셋
    const onActivity = () => {
      if (!minutesRef.current) return;
      void acquire();
      armTimer();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") onActivity();
    };

    if (minutes === 0) {
      clearTimer();
      releaseLock();
      return () => {
        released = true;
        clearTimer();
        releaseLock();
      };
    }

    // 시작 시 즉시 확보 + 타이머
    void acquire();
    armTimer();

    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener("pointerdown", onActivity, opts);
    window.addEventListener("touchstart", onActivity, opts);
    window.addEventListener("keydown", onActivity, opts);
    window.addEventListener("scroll", onActivity, opts);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      released = true;
      window.removeEventListener("pointerdown", onActivity, opts);
      window.removeEventListener("touchstart", onActivity, opts);
      window.removeEventListener("keydown", onActivity, opts);
      window.removeEventListener("scroll", onActivity, opts);
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimer();
      releaseLock();
    };
  }, [minutes]);

  return { minutes, setOption, supported: wakeLockSupported };
}
